import { Link, useParams } from 'react-router-dom'
import PageHero from '../components/PageHero'
import PageSeo from '../components/PageSeo'
import SiteFooter from '../components/SiteFooter'
import TopNav from '../components/TopNav'
import { getArticleBySlug, getRelatedArticles } from '../data/articles'
import {
  buildBlogPostingSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  getAbsoluteUrl,
  getArticlePath,
} from '../lib/seo'
import './ArticlePage.css'

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.contrastshower'
const APP_STORE_URL = 'https://apps.apple.com/us/app/contrast-shower/id6766412214'

const publishedDateFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

export default function ArticlePage() {
  const { slug = '' } = useParams()
  const article = getArticleBySlug(slug)

  if (!article) {
    return (
      <div className="article-page article-page--missing">
        <TopNav />
        <main className="article-missing">
          <div className="container">
            <p className="eyebrow eyebrow--blog">Article not found</p>
            <h1 className="article-missing__title">That article is not here.</h1>
            <p className="article-missing__body">
              The link might be old, or the page is still being written. Head back to the articles hub and pick another one.
            </p>
            <Link to="/articles" className="article-missing__link">
              Back to all articles
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    )
  }

  const relatedArticles = getRelatedArticles(article)
  const faqSchema = buildFaqSchema(article)

  return (
    <div className="article-page">
      <PageSeo
        title={article.seoTitle}
        description={article.seoDescription}
        canonical={getAbsoluteUrl(getArticlePath(article.slug))}
        image={getAbsoluteUrl(article.image.src)}
        type="article"
        keywords={article.keywords}
        schema={[buildOrganizationSchema(), buildBlogPostingSchema(article), ...(faqSchema ? [faqSchema] : [])]}
      />

      <TopNav />

      <main>
        <article className="article-shell">
          <PageHero as="header" innerClassName="article-hero__inner">
              <nav className="article-breadcrumbs" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <Link to="/articles">Articles</Link>
                <span>/</span>
                <span>{article.title}</span>
              </nav>

              <p className="eyebrow eyebrow--blog">{article.category}</p>
              <h1 className="article-hero__title heading-light">{article.title}</h1>
              <p className="article-hero__description p-intro">{article.description}</p>

              <div className="article-hero__meta">
                <span>{publishedDateFormatter.format(new Date(article.publishedAt))}</span>
                <span>{article.readingTime}</span>
              </div>
          </PageHero>

          <div className="container article-body-wrap">
            <div className="article-layout">
              <aside className="article-sidebar">
                <div className="article-sidebar__card">
                  <h2>Quick answer</h2>
                  <p>{article.quickAnswer}</p>
                </div>

                <div className="article-sidebar__card">
                  <h2>Key takeaways</h2>
                  <ul>
                    {article.takeaways.map((takeaway) => (
                      <li key={takeaway}>{takeaway}</li>
                    ))}
                  </ul>
                </div>

                <div className="article-sidebar__card article-sidebar__card--app">
                  <h2>Get the app</h2>
                  <p>Track your sessions and follow guided intervals.</p>
                  <div className="article-sidebar__store-buttons">
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="app-store-button w-button"></a>
                    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="play-store-button w-button"></a>
                  </div>
                </div>
              </aside>

              <div className="article-content">
                <figure className="article-cover">
                  <img src={article.image.src} alt={article.image.alt} className="article-cover__image" />
                </figure>

                <p className="article-intro">{article.intro}</p>

                {article.sections.map((section) => (
                  <section key={section.heading} className="article-section">
                    <h2>{section.heading}</h2>

                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}

                    {section.listItems?.length ? (
                      <div className="article-list-block">
                        {section.listTitle ? <h3>{section.listTitle}</h3> : null}
                        <ul>
                          {section.listItems.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {section.image ? (
                      <figure className="article-inline-image">
                        <img src={section.image.src} alt={section.image.alt} />
                        {section.image.caption ? <figcaption>{section.image.caption}</figcaption> : null}
                      </figure>
                    ) : null}
                  </section>
                ))}

                <section className="article-section article-faq">
                  <h2>FAQ</h2>
                  <div className="article-faq__items">
                    {article.faq.map((item) => (
                      <div key={item.question} className="article-faq__item">
                        <h3>{item.question}</h3>
                        <p>{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="article-cta">
                  <p className="article-cta__eyebrow">Want help sticking with it?</p>
                  <h2>Track sessions and follow guided protocols.</h2>
                  <p>
                    The Contrast Shower app gives you guided intervals, session tracking, and the habit-building tools that make consistency a lot easier than winging it every morning.
                  </p>
                  <div className="article-cta__buttons">
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="app-store-button w-button"></a>
                    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="play-store-button w-button"></a>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </article>

        <section className="related-articles">
          <div className="container">
            <div className="related-articles__header">
              <p className="eyebrow eyebrow--blog">Read next</p>
              <h2>Keep going while the water is still cold.</h2>
            </div>

            <div className="related-articles__grid">
              {relatedArticles.map((relatedArticle) => (
                <article key={relatedArticle.slug} className="related-card">
                  <Link to={`/articles/${relatedArticle.slug}`}>
                    <img src={relatedArticle.image.src} alt={relatedArticle.image.alt} className="related-card__image" />
                  </Link>
                  <div className="related-card__body">
                    <p className="related-card__meta">{relatedArticle.category}</p>
                    <h3>
                      <Link to={`/articles/${relatedArticle.slug}`}>{relatedArticle.title}</Link>
                    </h3>
                    <p>{relatedArticle.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  )
}
